import React from 'react';
import styled from 'styled-components';

import { tokens } from 'containers/Intelligence/theme';
import { scaledPx } from 'containers/Intelligence/theme/scale';

import { formatSharePercent, sharePercent } from '../formatMetric';
import { HoverTooltipSlot } from '../hoverTooltip';

// NOTE (conventions/repo-guidelines/patterns.md § When deviating, document why): this component does
// not wrap antd's `Progress type="circle"`. The design's ring is a two-size donut with a fixed Figma
// outer/hole geometry, a caller-supplied centre override that is often not the ring's own share
// (card 12 reads a ratio), and a caption below the figure — none of which antd's circle exposes; its
// stroke width is a single number and its centre is `format(percent)` only.
export type RadialPercentSize = 'sm' | 'md' | 'lg' | 'xl';

export interface IRadialPercentProps {
  /** Level as a 0-1 fraction, the same scale as `RankedBarList.pct` and `LegendTable.pct`. */
  percent: number;
  label: string;
  size: RadialPercentSize;
  /** A `tokens.color` value; without it the ring is painted in the family's ring hue. */
  color?: string;
  /** Pre-formatted centre override: card 12's ring reads a ratio (`1.42`), not its own share. */
  centerValue?: string;
  /**
   * PLA-8828: `3583-9503` prints the reading's name BESIDE the ring, above a band pill and a
   * movement the caption slot cannot hold. Set false there so the name is not drawn twice; `label`
   * is still required, because it is the ring's own name for assistive technology.
   */
  printLabel?: boolean;
  /** Inks for the centre figure, its caption and the unswept track, where a screen draws its own. */
  valueColor?: string;
  labelColor?: string;
  trackColor?: string;
  /** Shown above the ring while it is hovered or focused; the ring becomes focusable with it. */
  tooltip?: React.ReactNode;
  className?: string;
}

// `lg` and `sm` come from the component library sheet: 140/108 for the large widget and only the 64
// outer for `ring-sm`, where 48 keeps the small ring's stroke at 8px.
//
// `xl` is the SCREEN's own ring, read off the scene graph of Customer Engagement (node 589-30494,
// frame 589:27692): outer 210 with a 154 hole, so a 28px stroke. It is a fourth size rather than a
// correction to `lg` because the two other rings on the page (Experience & Value, Debt & Spending)
// have not been read yet — widening `lg` would resize them on one card's evidence.
const RING_GEOMETRY: Record<RadialPercentSize, { outer: number; hole: number }> = {
  lg: { outer: 140, hole: 108 },
  // PLA-9674: the Overwatch Rewards Fulfilment ring (5726:6545), outer 110 with an 82 hole.
  md: { outer: 110, hole: 82 },
  sm: { outer: 64, hole: 48 },
  xl: { outer: 210, hole: 154 }
};

// Per size, so `sm` and `lg` keep exactly the type they had. `xl`'s figure and caption are the
// screen's: Inter 700 34/40.8 in --is-text-title over Inter 400 10/12 in --is-text-caption.
const VALUE_TYPE: Record<
  RadialPercentSize,
  { size: string; weight: string; line: string; color: string }
> = {
  sm: {
    size: tokens.type['card-title-size'],
    weight: tokens.type['card-title-weight'],
    line: tokens.type['card-title-line'],
    color: tokens.color['text-primary']
  },
  md: {
    size: tokens.type['stat-size'],
    weight: tokens.type['stat-weight'],
    line: tokens.type['stat-line'],
    color: tokens.color['text-primary']
  },
  lg: {
    size: tokens.type['stat-size'],
    weight: tokens.type['stat-weight'],
    line: tokens.type['stat-line'],
    color: tokens.color['text-primary']
  },
  xl: {
    // A chart centre figure: the metric number step. PLA-8844.
    size: tokens.type['stat-size'],
    weight: tokens.type['card-title-weight'],
    line: tokens.type['stat-line'],
    color: tokens.color['text-title']
  }
};

const LABEL_TYPE: Record<RadialPercentSize, { size: string; line: string; color: string }> = {
  sm: {
    size: tokens.type['label-size'],
    line: tokens.type['label-line'],
    color: tokens.color['text-secondary']
  },
  md: {
    size: tokens.type['label-size'],
    line: tokens.type['label-line'],
    color: tokens.color['text-muted']
  },
  lg: {
    size: tokens.type['label-size'],
    line: tokens.type['label-line'],
    color: tokens.color['text-secondary']
  },
  xl: {
    size: tokens.type['caption-size'],
    line: tokens.type['caption-line'],
    color: tokens.color['text-caption']
  }
};

const RING_FILL = tokens.color['accent-blue'];

const holeMask = (size: RadialPercentSize): string => {
  // Scaled with the outer diameter below, or the stroke would thicken as the ring shrank.
  const radius = scaledPx(RING_GEOMETRY[size].hole / 2);

  return `radial-gradient(circle at center, transparent ${radius}, black ${radius})`;
};

const Root = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: ${tokens.space['8']};
  }
`;

/* PLA-8844: the ring follows --is-scale. At xl it is a 210px box, which does not fit the 197px grid
   track Customer Engagement leaves it once the Orbit panel is open. */
const RingBox = styled.div<{ $size: RadialPercentSize }>`
  position: relative;
  flex: 0 0 auto;
  width: ${({ $size }) => scaledPx(RING_GEOMETRY[$size].outer)};
  height: ${({ $size }) => scaledPx(RING_GEOMETRY[$size].outer)};
  max-width: 100%;
`;

/* The Figma library punches the ring's hole with a disc painted in the surface colour, which only
   works while the widget sits on the one surface it was drawn on — it is placed on both the card and
   the section surface here. Masking the hole out keeps it transparent on either. `black` is mask
   alpha, not a palette value. */
const Ring = styled.div<{ $size: RadialPercentSize; $measured: boolean; $track?: string }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: ${tokens.radius.pill};
  /* An unmeasured level paints a dashed track rather than a zero-swept solid one: a solid track is
     pixel-identical to a measured 0%, which is the misread this widget must not produce. */
  background: ${({ $measured, $track = tokens.color['track-bg'] }) =>
    $measured
      ? `conic-gradient(var(--radial-fill) 0deg, var(--radial-fill) var(--radial-sweep), ${$track} var(--radial-sweep), ${$track} 360deg)`
      : `repeating-conic-gradient(${$track} 0deg 6deg, transparent 6deg 12deg)`};
  -webkit-mask-image: ${({ $size }) => holeMask($size)};
  mask-image: ${({ $size }) => holeMask($size)};
`;

const Center = styled.div<{ $size: RadialPercentSize }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 ${tokens.space['8']};
  text-align: center;

  /* md stacks its caption flush under the figure, as 5726:6545 draws it. */
  > * + * {
    margin-top: ${({ $size }) => ($size === 'md' ? '0' : tokens.space['4'])};
  }
`;

const Value = styled.span<{ $size: RadialPercentSize; $color?: string }>`
  color: ${({ $size, $color }) => $color || VALUE_TYPE[$size].color};
  font-size: ${({ $size }) => VALUE_TYPE[$size].size};
  font-weight: ${({ $size }) => VALUE_TYPE[$size].weight};
  line-height: ${({ $size }) => VALUE_TYPE[$size].line};
`;

const Label = styled.span<{ $size: RadialPercentSize; $color?: string }>`
  color: ${({ $size, $color }) => $color || LABEL_TYPE[$size].color};
  font-size: ${({ $size }) => LABEL_TYPE[$size].size};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${({ $size }) => LABEL_TYPE[$size].line};
`;

const RadialPercent: React.FC<IRadialPercentProps> = ({
  percent,
  label,
  size,
  color,
  centerValue,
  printLabel = true,
  valueColor,
  labelColor,
  trackColor,
  tooltip,
  className
}) => {
  const [tooltipShown, setTooltipShown] = React.useState(false);
  const hasLevel = Number.isFinite(percent);

  // An unmeasured level has no arc to draw and no figure of its own to print: the card shows its own
  // state block instead. A caller-supplied centre figure is not the ring's own share (card 12 reads
  // a ratio), so it still stands — inside a ring marked unmeasured rather than swept to zero.
  if (!hasLevel && !centerValue) {
    return null;
  }

  const fill = color || RING_FILL;
  // Swept from the printed figure so the arc and the caption can never disagree; rounded to two
  // decimals because 74.8% of a turn is not exactly representable and CSS gains nothing from the tail.
  const sweep = hasLevel ? Math.round(sharePercent(percent) * 360) / 100 : 0;
  const ringVariables = {
    '--radial-fill': fill,
    '--radial-sweep': `${sweep}deg`
  } as React.CSSProperties;

  const caption = (
    <Label $size={size} $color={labelColor} data-testid="radial-percent-label">
      {label}
    </Label>
  );

  return (
    <Root
      className={className}
      data-measured={hasLevel}
      data-size={size}
      data-testid="radial-percent"
    >
      <RingBox
        $size={size}
        {...(tooltip
          ? {
              tabIndex: 0,
              onMouseEnter: () => setTooltipShown(true),
              onMouseLeave: () => setTooltipShown(false),
              onFocus: () => setTooltipShown(true),
              onBlur: () => setTooltipShown(false)
            }
          : {})}
      >
        {tooltip && tooltipShown ? (
          <HoverTooltipSlot
            role="tooltip"
            style={{ left: '50%' }}
            data-testid="radial-percent-tooltip"
          >
            {tooltip}
          </HoverTooltipSlot>
        ) : null}
        <Ring
          $measured={hasLevel}
          $size={size}
          $track={trackColor}
          // The level is printed in the middle of the ring; announcing the arc repeats it.
          aria-hidden="true"
          data-color={fill}
          data-measured={hasLevel}
          data-sweep={sweep}
          data-testid="radial-percent-ring"
          style={ringVariables}
        />
        <Center $size={size}>
          <Value $size={size} $color={valueColor} data-testid="radial-percent-value">
            {centerValue || formatSharePercent(percent)}
          </Value>
          {size === 'sm' || !printLabel ? null : caption}
        </Center>
      </RingBox>
      {size === 'sm' && printLabel ? caption : null}
    </Root>
  );
};

export default RadialPercent;
